import {
  EmojiEmotionsOutlined,
  FilterAltOutlined,
  Phone as PhoneIcon,
  PriorityHigh,
  SafetyCheckOutlined,
  ScoreboardOutlined,
  Stars
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Paper,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { BarChartPro as BarChart } from "@mui/x-charts-pro/BarChartPro";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { HISTOGRAM_COLORS } from "../../common/colors";
import { useFilters } from "../../common/hooks/useFilters";
import { FeatureFlag } from "../../common/posthog/FeatureFlag";
import FilterBar from "../../components/FilterBar";
import Histogram from "../../components/Histogram";
import MetricCard from "../../components/MetricCard";
import MetricPanel from "../../components/MetricPanel";
import ProtocolPerformanceTable from "../../components/ProtocolPerformanceTable";
import { useDashboardData } from "../../data/hooks/useDashboardData";
import { CONVERSATION_LENS } from "../../utils/terminology";
import { getUserTimezone } from "../../utils/timezone";
import { useSelectedGroups } from "../auth/hooks/useSelectedGroups";
import UnprocessedCallsSection from "./UnprocessedCallsSection";
import { convertFiltersToURLSearchParams } from "./utils";

const Dashboard = () => {
  // Use the shared hook with dashboard-specific defaults
  const { filters, updateFilter } = useFilters({
    defaults: {
      processDecision: "DO_PROCESS",
      callType: "external"
    }
  });

  const timezone = getUserTimezone();
  const navigate = useNavigate();
  const { permittedEmails } = useSelectedGroups();

  // Use React Query hook for all dashboard data
  const { data: dashboardData, isFetching: isLoading, error: queryError, refetch } = useDashboardData({
    timeRange: filters.timeRange,
    timezone,
    protocol: filters.protocol,
    user: filters.user,
    callType: filters.callType,
    permittedEmails
  });

  const metrics = dashboardData?.metrics || null;
  const chartData = dashboardData?.chartData || null;
  const protocolPerformance = dashboardData?.protocolPerformance || [];
  const error = queryError ? (queryError as Error).message : null;

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const navigateToExplore = (additionalFilter: { key: string; value: string } | { key: string; values: Record<string, string> }) => {
    // Convert filters to the correct type for convertFiltersToURLSearchParams
    const dashboardFilters = {
      timeRange: filters.timeRange,
      sentiment: filters.sentiment,
      relevance: filters.relevance,
      risk: filters.risk,
      protocol: filters.protocol,
      user: filters.user,
      processDecision: filters.processDecision,
      callType: filters.callType
    };

    // Create search params from current filters
    const searchParams = convertFiltersToURLSearchParams(dashboardFilters);

    // Add additional filter(s)
    if ("value" in additionalFilter) {
      // Single filter case
      searchParams.set(additionalFilter.key, additionalFilter.value);
    } else if ("values" in additionalFilter) {
      // Multiple filters case
      Object.entries(additionalFilter.values).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
    }

    navigate(`/explore?${searchParams.toString()}`);
  };

  const handleHistogramClick = (filterKey: string, value: string) => {
    if (filterKey === "sc_overall_score") {
      const [min, max] = value.split("-").map(Number);

      navigateToExplore({
        key: "score_range",
        values: {
          min_score: min.toString(),
          max_score: max.toString()
        }
      });
    } else {
      navigateToExplore({ key: filterKey, value });
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <FilterBar
        timeRange={filters.timeRange}
        sentiment={filters.sentiment}
        relevance={filters.relevance}
        risk={filters.risk}
        protocol={filters.protocol}
        user={filters.user}
        processDecision={filters.processDecision}
        callType={filters.callType}
        totalConversations={metrics?.totalConversations || 0}
        onFilterChange={updateFilter}
        onRefresh={handleRefresh}
        showFilters={{
          timeRange: true,
          protocol: true,
          user: true,
          callType: true,
          // Only show these filters on the Dashboard
          sentiment: false,
          relevance: false,
          risk: false,
          processDecision: false
        }}
      />

      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Distribution Charts Row with Metric Panel */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* All panels with equal width */}
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <MetricPanel
            title="Conversations"
            subTitle={`${metrics?.totalConversations.toLocaleString() || "0"} ingested`}
            icon={<PhoneIcon />}
            mainValueLabel="Processed"
            mainValue={`${metrics?.processedConversations.toLocaleString() || "0"}`}
            subValue1Label="Scored for performance"
            subValue1={`${metrics?.totalScored.toLocaleString() || "0"}`}
            subValue2Label="Analyzed for risk"
            subValue2={`${metrics?.processedForRisk.toLocaleString() || "0"}`}
            onClick={() => !isLoading && metrics && navigateToExplore({ key: "time_range", value: filters.timeRange })}
            isLoading={isLoading}
          />
        </Grid>

        {/* Histograms - all with equal width */}
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <Histogram
            title={`${CONVERSATION_LENS} scores`}
            subTitle={`${metrics?.totalScored.toLocaleString() || "0"} scored`}
            icon={<ScoreboardOutlined />}
            buckets={[metrics?.performance["0_1"] || 0, metrics?.performance["1_2"] || 0, metrics?.performance["2_3"] || 0, metrics?.performance["3_4"] || 0, metrics?.performance["4_5"] || 0]}
            labels={["0-1", "1-2", "2-3", "3-4", "4-5"]}
            colors={HISTOGRAM_COLORS.score}
            textColors={HISTOGRAM_COLORS.scoreText}
            showPercentages={true}
            height={180}
            filterKey="sc_overall_score"
            onFilterClick={!isLoading ? handleHistogramClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <Histogram
            title="Risk distribution"
            subTitle={`${metrics?.processedForRisk.toLocaleString() || "0"} analyzed`}
            icon={<SafetyCheckOutlined />}
            buckets={[metrics?.risk.low || 0, metrics?.risk.medium || 0, metrics?.risk.high || 0]}
            labels={["Low", "Medium", "High"]}
            colors={HISTOGRAM_COLORS.risk}
            textColors={HISTOGRAM_COLORS.riskText}
            showPercentages={true}
            height={180}
            filterKey="ra_overall_indicator"
            onFilterClick={!isLoading ? handleHistogramClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <Histogram
            title="Sentiment"
            subTitle={`${metrics?.processedConversations.toLocaleString() || "0"} processed`}
            icon={<EmojiEmotionsOutlined />}
            buckets={[metrics?.sentiment.negative || 0, metrics?.sentiment.neutral || 0, metrics?.sentiment.positive || 0]}
            labels={["Negative", "Neutral", "Positive"]}
            colors={[
              "#F44336", // Red for negative
              "#FFB300", // Yellow for neutral
              "#4CAF50" // Green for positive
            ]}
            textColors={[
              "#FFFFFF", // White text for red
              "#000000", // Black text for yellow
              "#FFFFFF" // White text for green
            ]}
            showPercentages={true}
            height={180}
            filterKey="sentiment"
            onFilterClick={!isLoading ? handleHistogramClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <Histogram
            title="Relevance"
            subTitle={`${metrics?.processedConversations.toLocaleString() || "0"} processed`}
            icon={<FilterAltOutlined />}
            buckets={[metrics?.relevance.low || 0, metrics?.relevance.medium || 0, metrics?.relevance.high || 0]}
            labels={["Low", "Medium", "High"]}
            colors={[
              "#2196f3", // Blue for low relevance
              "#FFB300", // Yellow for medium relevance
              "#4CAF50" // Green for high relevance
            ]}
            textColors={[
              "#FFFFFF", // White text for blue
              "#000000", // Black text for yellow
              "#FFFFFF" // White text for green
            ]}
            showPercentages={true}
            height={180}
            filterKey="relevance"
            onFilterClick={!isLoading ? handleHistogramClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            icon={<Stars />}
            title="Average Score"
            value={metrics?.averageScore.toFixed(2) || "0.00"}
            trend={undefined}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            icon={<PriorityHigh color="error" />}
            title="High Risks"
            value={metrics?.risk.high.toLocaleString() || "0"}
            trend={undefined}
            onClick={() => !isLoading && metrics && navigateToExplore({ key: "ra_overall_indicator", value: "high" })}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Protocol Performance Table */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12 }}>
          <ProtocolPerformanceTable
            timeRange={filters.timeRange}
            timezone={timezone}
            user={filters.user}
            data={protocolPerformance}
            onClick={(key, value) => !isLoading && navigateToExplore({ key, value })}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Sentiment Distribution by Relevance</Typography>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <Box sx={{ display: "inline-block", position: "relative", width: 40, height: 40 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "4px solid rgba(25, 118, 210, 0.2)",
                      borderRadius: "50%",
                      borderTop: "4px solid rgba(25, 118, 210, 1)",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : chartData ? (
              <BarChart
                xAxis={[{
                  data: ["low", "medium", "high"],
                  scaleType: "band"
                }]}
                series={[
                  { data: chartData.sentimentDistribution.positive, label: "Positive", stack: "default", stackOffset: "expand", color: "#4CAF50" },
                  { data: chartData.sentimentDistribution.neutral, label: "Neutral", stack: "default", stackOffset: "expand", color: "#FFB300" },
                  { data: chartData.sentimentDistribution.negative, label: "Negative", stack: "default", stackOffset: "expand", color: "#F44336" }
                ]}
                height={300}
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <Typography variant="body1" color="text.secondary">No data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Score Distribution by Relevance</Typography>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <Box sx={{ display: "inline-block", position: "relative", width: 40, height: 40 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "4px solid rgba(25, 118, 210, 0.2)",
                      borderRadius: "50%",
                      borderTop: "4px solid rgba(25, 118, 210, 1)",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : chartData ? (
              <BarChart
                xAxis={[{
                  data: ["low", "medium", "high"],
                  scaleType: "band"
                }]}
                series={[
                  { data: chartData.scoreDistribution.s1, label: "0-1", stack: "default", stackOffset: "expand", color: "#F44336" },
                  { data: chartData.scoreDistribution.s2, label: "1-2", stack: "default", stackOffset: "expand", color: "#FF7043" },
                  { data: chartData.scoreDistribution.s3, label: "2-3", stack: "default", stackOffset: "expand", color: "#FFB300" },
                  { data: chartData.scoreDistribution.s4, label: "3-4", stack: "default", stackOffset: "expand", color: "#8BC34A" },
                  { data: chartData.scoreDistribution.s5, label: "4-5", stack: "default", stackOffset: "expand", color: "#4CAF50" }
                ]}
                height={300}
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <Typography variant="body1" color="text.secondary">No data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <FeatureFlag flag="unprocessed-calls-dashboard" fallback={null}>
        <UnprocessedCallsSection
          timeRange={filters.timeRange}
          timezone={timezone}
          protocol={filters.protocol}
          user={filters.user}
          callType={filters.callType}
          permittedEmails={permittedEmails}
        />
      </FeatureFlag>
    </Box>
  );
};

export default Dashboard;
